import { withErrorCode } from '@/lib/errors'
import { formatUserFacingError } from '@/lib/errors'
import { useState } from 'react'
import { Link } from 'react-router-dom'
import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { z } from 'zod'
import { Mail, Search } from 'lucide-react'
import { HoldConfirmButton } from '@/components/ui/hold-confirm-button'
import { MagicInput as Input } from '@/components/ui/magic-input'
import { Label } from '@/components/ui/label'
import { ticketingService } from '../services/ticketing.service'

const lookupSchema = z.object({
  email: z.string().email('Enter a valid email address'),
})

type LookupValues = z.infer<typeof lookupSchema>

export function TicketLookupPage() {
  const [requestAccepted, setRequestAccepted] = useState(false)
  const [lookupError, setLookupError] = useState<string | null>(null)

  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitting },
  } = useForm<LookupValues>({
    resolver: zodResolver(lookupSchema),
  })

  async function onSubmit(values: LookupValues) {
    setLookupError(null)
    setRequestAccepted(false)
    try {
      await ticketingService.requestOrdersByEmail(values.email)
      setRequestAccepted(true)
    } catch (err: unknown) {
      setLookupError(formatUserFacingError(err, 'Failed to look up tickets'))
    }
  }

  return (
    <div className="container mx-auto max-w-lg px-4 py-16 space-y-8">
      <div>
        <h1 className="text-3xl font-bold">Find My Tickets</h1>
        <p className="mt-2 text-muted-foreground">
          Enter the email address you used when purchasing tickets.
        </p>
      </div>

      <form onSubmit={(e) => void handleSubmit(onSubmit)(e)} className="space-y-4">
        <div className="space-y-1">
          <Label htmlFor="email">Email Address</Label>
          <div className="flex gap-2">
            <Input
              id="email"
              type="email"
              placeholder="you@example.com"
              {...register('email')}
              className="flex-1"
            />
            <HoldConfirmButton type="submit" disabled={isSubmitting}>
              <Search className="h-4 w-4 mr-2" />
              {isSubmitting ? 'Searching…' : 'Search'}
            </HoldConfirmButton>
          </div>
          {errors.email && <p className="text-sm text-destructive">{withErrorCode(String(errors.email.message), 'app/validation')}</p>}
        </div>
        {lookupError && <p className="text-sm text-destructive">{lookupError}</p>}
      </form>

      {requestAccepted && (
        <div className="rounded-lg border bg-card p-5 text-center space-y-2">
          <Mail className="mx-auto h-7 w-7 text-primary" />
          <p className="font-medium">Check your email</p>
          <p className="text-sm text-muted-foreground">
            If paid ticket orders match that address, we sent fresh links to download them.
          </p>
        </div>
      )}

      <div className="border-t pt-6 text-center text-sm text-muted-foreground">
        Have an account?{' '}
        <Link to="/login" className="underline hover:text-foreground">
          Sign in
        </Link>{' '}
        to view all your tickets in one place.
      </div>
    </div>
  )
}
